import React, { useState, useEffect, useCallback } from 'react'; // Import useState, useEffect
import Wrapper from './Wrapper';
import EmailNotificationPreference from 'components/page-components/actions/EmailNotificationPreference';
import { useGetAgent } from './hooks/useGetAgent';
import { useParams } from 'react-router-dom'; // Import useLocation and useNavigate
import { useError } from 'error-provider/ErrorProvider';
import { useGetMeQuery } from 'graphql/generated';
import TextNotifications from 'components/page-components/actions/TextNotifications';
import TerminateCallActions from 'components/page-components/actions/TerminateCallActions';
import FillAudio from 'components/page-components/actions/FillAudio';
import { Stack } from '@chakra-ui/react'; // Import Flex, Spinner, Text
import GoogleCalendar from 'components/page-components/actions/GoogleCalendar';
import TranscriberKeywords from 'components/page-components/actions/TranscriberKeywords';
import Loader from './Loader';
import useServerApi from 'hooks/useServerApi'; // Added import

const MoreSettings = () => {
    const params = useParams();
    const { showError } = useError();
    const { data, loading } = useGetAgent();
    const [{ get, post }] = useServerApi(); // Initialize server API hook
    const [emailData, setEmailData] = useState<any[]>([]); // State for email data
    const [emailDataLoading, setEmailDataLoading] = useState(false); // Loading state for email data
    const [emailEnabled, setEmailEnabled] = useState(false); // State for email enabled status
    const agentId = data?.id;
    useGetMeQuery({
        onError(error: any) {
            showError(undefined, error);
        },
    });

    const fetchEmailData = useCallback(async () => {
        if (!agentId) {
            console.error('No agent ID found in data:', data);
            return;
        }
        setEmailDataLoading(true);
        try {
            const [success, response, error] = await get(`/rest_endpoints/notificationPreference/emails/${agentId}`);
            if (success && response) {
                setEmailData(response.emails || []);
                setEmailEnabled(response.enabled || false);
            } else {
                showError('Failed to load email notification preferences', error);
            }
        } catch (error) {
            console.error('Error fetching email data:', error);
            showError('Failed to load email notification preferences', error instanceof Error ? error : new Error(String(error)));
        } finally {
            setEmailDataLoading(false);
        }
    }, [agentId, data, get, showError]);
    
    useEffect(() => {
        if (agentId) {
            fetchEmailData();
        }
    }, [agentId, fetchEmailData]); // Empty dependency array means this runs once on mount



    const handleEmailPreferenceSave = async (payloadData: any) => {
        if (!payloadData) return;
        try {
            setEmailDataLoading(true);
            const agentId = params.id;
            const payload = {
                enabled: payloadData.enabled !== undefined ? payloadData.enabled : true,
                emails: payloadData.emails || payloadData,
            };
            const [success, response, error] = await post(`/rest_endpoints/notificationPreference/emails/${agentId}/save`, payload);
            if (success && response) {
                // Refresh email data from server after save
                await fetchEmailData();
            } else {
                showError('Failed to save email notification preferences', error);
            }
        } catch (error) {
            showError('Failed to save email notification preferences', error instanceof Error ? error : new Error(String(error)));
        } finally {
            setEmailDataLoading(false);
        }
    };

    // Standard loading check
    if (loading || !data) {
        return <Loader />;
    }


    return (
        <Wrapper title='More Settings' description='Configure additional agent settings'>
            <Stack spacing='5'>
                <EmailNotificationPreference
                    existingEmailData={emailData}
                    onSave={handleEmailPreferenceSave}
                    emailEnabled={emailEnabled}
                    isLoading={emailDataLoading}
                />
                <TextNotifications data={data.textNotification} agentId={params.id} />
                <TranscriberKeywords agentId={params.id} />
                <TerminateCallActions data={data.terminateCallAction} agentId={params.id} />
                <FillAudio data={data.fillerAudio} isPremium={true} agentId={params.id} />
                <GoogleCalendar actionsData={data.googleCalendar} />
            </Stack>
        </Wrapper>
    );
};

export default MoreSettings;
